import * as store from '@attain-apps/fe/web/main/lib/store'
import { TypeChecker } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import type { Plugin } from '../plugin'

const SOURCE = 'restoreUnlistedMessages (effect)'

/**
 * Puts back the conversation's messages known here that its list is missing.
 *
 * Changing conversation empties the cache and the list, and the read that
 * follows replaces the list with what storage holds. A message's first one
 * starts a new conversation, and does so while its messages are still being
 * saved, so that read can come back without them. Their own publications went
 * out before the new conversation was subscribed to, so nothing else brings
 * them back.
 *
 * Runs once each read has finished, taking what the cache holds and what was
 * sent from here, and listing whatever is missing in the order it was created.
 */
export const restoreUnlistedMessages = store.effects.create(
  (feature: Plugin['feature'], plugin: Plugin) =>
    // Not on the conversation changing itself: the read it starts is still in
    // flight then, and listing messages ends the list's loading state, before
    // the view has seen it begin and while it is showing its loader.
    rxjs
      .merge(
        // The read succeeded: the list has just been replaced by the ids
        // storage returned, which may not include messages still being saved.
        plugin.collection.plugins.currentListPlugin.feature.actions.load.success
          .observe()
          .pipe(rxjs.map(() => ({ succeeded: true }))),
        // The read failed: the list holds only what publications have admitted
        // since the conversation changed.
        plugin.collection.plugins.currentListPlugin.feature.actions.load.error
          .observe()
          .pipe(rxjs.map(() => ({ succeeded: false }))),
      )
      .pipe(
        // Effects see an action after its reducers have run, so these are the
        // values the read left behind.
        rxjs.withLatestFrom(
          plugin.collection.feature.selectors.context.asObservable,
          plugin.collection.plugins.cachePlugin.feature.selectors.lookup
            .asObservable,
          plugin.collection.plugins.currentListPlugin.feature.selectors.ids
            .asObservable,
          feature.state.asObservable,
        ),
        // Serialized so each pass works from what the previous one dispatched.
        rxjs.concatMap(
          ([{ succeeded }, context, cached, listedIds, { sentById }]) => {
            // A new conversation's view has no id until the conversation is
            // published, and holds nothing a read could have dropped.
            const conversationId = context?.conversationId
            if (!conversationId) {
              return rxjs.EMPTY
            }

            const listed = new Set(listedIds)
            // Messages sent from here are kept for every conversation they
            // were sent in; only the current one's are of interest.
            const sent = Object.values(sentById).filter(
              message => message.conversationId === conversationId,
            )
            // What the cache holds is newer than what was sent, as it has taken
            // in the message's publications since.
            const uncachedSent = sent.filter(
              message => !cached[message.id] && !listed.has(message.id),
            )
            // Everything known here for the conversation that the list is
            // missing: messages the cache holds (such as an agent message
            // published while the read was in flight), plus sent messages the
            // conversation changing took out of the cache.
            const unlisted = [
              ...Object.values(cached)
                .filter(TypeChecker.isNotNullish.raw)
                .filter(
                  message =>
                    message.conversationId === conversationId &&
                    !listed.has(message.id),
                ),
              ...uncachedSent,
            ]
            // Only a successful read confirms that storage holds a sent
            // message.
            const listedSent = succeeded
              ? sent.filter(message => listed.has(message.id))
              : []

            return rxjs.from([
              // The conversation changing emptied the cache, so sent messages
              // go back into it before the list can show them.
              ...(uncachedSent.length > 0
                ? [
                    plugin.collection.plugins.cachePlugin.feature.actions.put
                      .from(SOURCE)
                      .create({ entities: uncachedSent }),
                  ]
                : []),
              // A message read before its entries were saved lists none of
              // them; the ones it was sent with stand in until they arrive.
              ...listedSent.map(message =>
                plugin.collection.plugins.cachePlugin.feature.actions.patch
                  .from(SOURCE)
                  .create({
                    id: message.id,
                    // A listed message that has entries is left as the read
                    // stored it; only an empty one is filled in.
                    update: listedMessage =>
                      listedMessage?.info.length === 0
                        ? { ...listedMessage, info: message.info }
                        : listedMessage,
                  }),
              ),
              // Only when something is missing. The list is set whole rather
              // than appended to, as a failed read leaves it holding just the
              // messages published since, which can be newer than the ones
              // put back; creation order places each correctly either way.
              ...(unlisted.length > 0
                ? [
                    plugin.collection.plugins.currentListPlugin.feature.actions.set
                      .from(SOURCE)
                      .create({
                        ids: [
                          ...listedIds
                            .map(id => cached[id])
                            .filter(TypeChecker.isNotNullish.raw),
                          ...unlisted,
                        ]
                          .sort(
                            (a, b) =>
                              (a.createdAt?.getTime() ?? 0) -
                              (b.createdAt?.getTime() ?? 0),
                          )
                          .map(message => message.id),
                      }),
                  ]
                : []),
              // Storage now holds these, so they need not be kept here. A send
              // that failed is never listed, and stays, so its bubble comes
              // back when the conversation is reopened.
              ...(listedSent.length > 0
                ? [
                    feature.actions.dropSent
                      .from(SOURCE)
                      .create({ ids: listedSent.map(message => message.id) }),
                  ]
                : []),
            ])
          },
        ),
      ),
)
